import React from 'react';

import { Alert, Form, Col, Button, InputGroup, Card, Row, Container, Tooltip, OverlayTrigger } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { validate, ValidationError } from 'class-validator';
import { Client, Ethnicity, Gender, User, Menopause } from '../../models';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClientEntryStore } from '../../stores/client-entry-store';

interface ClientEntryProps {
  client: Client;
  currentUser: User;
  clientEntryStore: ClientEntryStore;
  saveOrUpdate: () => Promise<Client>;
  expandAddClient: boolean;
  history?: any;
}

type State = {
  validationErrors: Map<string, string>;
  alerts: Array<any>,
  expandAddClient: boolean;
  dateOfBirth: string;
  panelTitle: string;
};

export class ClientEntry extends React.Component<ClientEntryProps, State> {
  state: State = {
    validationErrors: new Map<string, string>(),
    alerts: [],
    expandAddClient: this.props.expandAddClient,
    dateOfBirth: undefined,
    panelTitle: ClientEntry.panelTitle(this.props.client),
  };

  static getDerivedStateFromProps(nextProps) {
    const { client } = nextProps;

  // ✅ Set defaults if undefined
  if (client.cigaretteSmoker == null) client.cigaretteSmoker = false;
  if (client.alcoholDrinker == null) client.alcoholDrinker = false;

    let expandAddClient = nextProps.expandAddClient;

    return {
      dateOfBirth: client.dateOfBirth ? moment(client.dateOfBirth).toDate() : undefined,
      expandAddClient,
      panelTitle: ClientEntry.panelTitle(client)
    };
  }

  static panelTitle(client: Client): string {
    return (client && client.isNew()) ? 'Add Client' : 'Edit Client';
  }

  handleInputChange = (event: any) => {
    const { client } = this.props;
    const key = event.target.name;
    let value = event.target.value;
    let nbr: number;

    if (key === 'elevation' || key === 'cigarettesSmoked' || key === 'trimester' || key === 'standardDrinks') {
      if (value.trim().length === 0) {
        nbr = undefined;
      } else {
        nbr = Number(value.replace(/\D/g, ''));
      }
    }

    if (key === 'cigaretteSmoker' || key === 'alcoholDrinker') {
      nbr = undefined;
      value = value === 'true';
    }

    if (isNaN(nbr)) {
      client[key] = value;
    } else {
      client[key] = nbr;
    }

    console.log(this.props);
    this.clearAlerts();
  }

  handleDateOfBirthChange = (value: any) => {
    const { client } = this.props;
    if (value == null) {
      client.dateOfBirth = undefined;
    } else {
      const startOfDay = moment(value).startOf('day').toISOString();
      client.dateOfBirth = startOfDay;
    }

    this.setState({
      dateOfBirth: value
    })

    console.log(client);
  }

  saveOrUpdate = (event: any) => {
    event.preventDefault();
    this.validateAndSaveClient();
  }

  validateAndSaveClient() {
    const { client } = this.props;
    const validationErrors = new Map<string, string>();   
    console.log(client);

    // Special check for pregnant female and age
    if (client.isTooYoungToBePregnant()) {
      validationErrors.set('dateOfBirth', 'Client must be 15 years of age or older when pregnant is selected.');
    }

    // Special check for age
    if (client.isTooOld()) {
      validationErrors.set('dateOfBirth', 'Client can not be over 200 years of age.');
    }

    if (validationErrors.size > 0) {
      this.setState({
        validationErrors
      });
      return;
    }

    validate(client).then((errors: ValidationError[]) => { // errors is an array of validation errors
      if (errors.length > 0 || validationErrors.size > 0) {
        for (let error of errors) {
          this.displayErrorMessage(error, validationErrors);
        }
        this.setState({
          validationErrors
        });
      } else {
        this.setState({
          validationErrors: new Map<string, string>()
        });
        this.saveOrUpdateClient();
      }
    });
  }

  saveOrUpdateClient() {
    this.props.client.practitionerId = this.props.currentUser.subject;
    // If a non-smoker, set cigarettes smoked to 0.
    if (!this.props.client.cigaretteSmoker) {
      this.props.client.cigarettesSmoked = 0;
    }

    this.props.saveOrUpdate().then((updatedClient) => {
      console.log(updatedClient);
      this.clearForm(false);
      this.addAlert('success', 'Successfully updated client!', false, 5000);
      this.props.clientEntryStore.setExpandAddClient(false);
    }).catch((error) => {
      this.addAlert('danger', 'Failed to add/edit client');
    });
  }

  clearForm = (clearAlerts?: boolean) => {
    const { clientEntryStore } = this.props;
    clientEntryStore.resetCurrentClient();
    
    this.setState({
      dateOfBirth: undefined,
      validationErrors: new Map<string, string>()
    });

    if (clearAlerts) {
      this.clearAlerts();
    }
  }

  clearAlerts = () => {
    console.log('clearAlerts was called....');
    this.setState({
      alerts: []
    });
  }

  openElevationInfoWindow = () => {
    window.open('https://labsmarts.com/bibliography/#highelevation');
  }

  openStandardDrinkInfoWindow = () => {
    window.open('https://www.niaaa.nih.gov/what-standard-drink');
  }

  displayErrorMessage(error: ValidationError, validationErrors: Map<string, string>) {
    for (let constraint of Object.keys(error.constraints)) {
      validationErrors.set(error.property, error.constraints[constraint]);
    }
  }

  renderErrorListItems(validationErrors: Map<string, string>) {
    let listItems: JSX.Element[] = [];
    validationErrors.forEach((value: string, fieldName: string) => {
      if (validationErrors.get(fieldName)) {
        listItems.push(<li key={fieldName + '-error'}>{validationErrors.get(fieldName)}</li>);
      }
    });

    return listItems;
  }

  renderValidationErrors(validationErrors: Map<string, string>) {
    return (
      <div className="panel">
        <div className="validation-errors">
          <ul>
            {this.renderErrorListItems(validationErrors)}
          </ul>
        </div>
      </div>
    );
  }
  
  renderAlerts() {
    console.log('renderAlerts called....');
    const alerts = this.state.alerts.map((alert: any, index: number) => {
      return this.renderAlert(alert.type, alert.text, index);
    });

    return (
      <div>{alerts}</div>
    );
  }

  renderAlert(type: any, text: string, index: number) {
    return (
      <Alert key={index} variant={type} dismissible>
        {text}
      </Alert>
    );
  }

  renderErrors() {
    const { validationErrors } = this.state;
    return validationErrors.size > 0 ? this.renderValidationErrors(validationErrors) : (null);
  }

  addAlert = (type: string, text: string, keepCurrentAlerts: boolean = false, autoClose?: number) => {
    console.log('add alert called....');
    const alert = { type, text };
    const alerts = keepCurrentAlerts ? [...this.state.alerts, alert] : [alert];
    this.setState({
      alerts
    });

    if (autoClose) {
      setTimeout(() => { this.clearAlerts(); }, autoClose);
    }
  }

  gender(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} column="true" controlId="genderGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Sex</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="gender1"
            name="gender" 
            value={Gender.MALE}
            checked={client.gender === Gender.MALE}  
            onChange={this.handleInputChange}
            label="Male" 
            type="radio"
            isInvalid={!!validationErrors.get('gender')}
          />
          <Form.Check
            inline
            id="gender2"
            name="gender"
            value={Gender.FEMALE}
            checked={client.gender === Gender.FEMALE}  
            onChange={this.handleInputChange}
            label="Female" 
            type="radio"
            isInvalid={!!validationErrors.get('gender')}
          />
        </Col>
      </Form.Group>
    );
  }

  menopause(client: Client, validationErrors: any, col1: number, col2: number) {
    if (client.gender !== Gender.FEMALE) {
      // In case the user previously selected Female and set Menopause, we need to clear it
      // Also, the user may have selected a trimester so we need to clear it as well
      client.menopause = undefined;
      client.trimester = undefined;
      return null;
    }

    return (
      <Form.Group as={Row} controlId="menopauseGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Menopause/Pregnant</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="menopause1"
            name="menopause" 
            value={Menopause.PREMENOPAUSE}
            checked={client.menopause === Menopause.PREMENOPAUSE}  
            onChange={this.handleInputChange}
            label="Premenopause" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
          <Form.Check
            inline
            id="menopause2"
            name="menopause"
            value={Menopause.POSTMENOPAUSE}
            checked={client.menopause === Menopause.POSTMENOPAUSE}  
            onChange={this.handleInputChange}
            label="Postmenopause" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
          <Form.Check
            id="menopause3"
            inline
            name="menopause"
            value={Menopause.PREGNANT}
            checked={client.menopause === Menopause.PREGNANT}  
            onChange={this.handleInputChange}
            label="Pregnant" 
            type="radio"
            isInvalid={!!validationErrors.get('menopause')}
          />
        </Col>
      </Form.Group>
    );
  }

  trimester(client: Client, validationErrors: any, col1: number, col2: number) {
    if (client.menopause !== Menopause.PREGNANT) {
      // In case the user previously selected a trimester, we need to clear it
      client.trimester = undefined;
      return null;
    }

    // Default trimester so validation rules are satisfied
    if (client.trimester == null) {
      client.trimester = NaN;
    }
  
    return (
      <Form.Group as={Row} controlId="trimesterGroup">
        <Form.Label column sm={col1}>Trimester</Form.Label>
        <Col sm={col2}>
          <Form.Control as="select"
            name="trimester"
            value={client.trimester.toString()}
            onChange={this.handleInputChange}
            isInvalid={!!validationErrors.get('trimester')}
          >
            <option value="NaN">Select Trimester</option>
            <option value="1">First</option>
            <option value="2">Second</option>
            <option value="3">Third</option>
            <option value="0">Unknown</option>
          </Form.Control>
        </Col>  
      </Form.Group>
    );
  }

  dateOfBirth(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="dateOfBirthGroup">
        <Form.Label column sm={col1}>Date of Birth</Form.Label>
        <Col sm={col2}>
          {/* TODO: Look into using a customInput here so that validation styling is applied. */}
          <DatePicker
            selected={this.state.dateOfBirth}
            onChange={this.handleDateOfBirthChange}
            maxDate={new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            todayButton
            dropdownMode="select"
            dateFormat=" MM/dd/yyyy"
            placeholderText=" MM/DD/YYYY"
            popperPlacement="bottom"
            popperModifiers={{
                flip: {
                    behavior: ["bottom"] // don't allow it to flip to be above
                },
                preventOverflow: {
                    enabled: false // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
                },
                hide: {
                    enabled: false // turn off since needs preventOverflow to be enabled
                }
            }}
          />
        </Col>
      </Form.Group>
    );
  }

  ethnicity(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="ethnicityGroup">
        <Form.Label column sm={col1}>Ethnicity</Form.Label>
        <Col sm={col2}>
          <Form.Control as="select"
            name="ethnicity"
            placeholder="Select Ethnicity"
            value={client.ethnicity}
            onChange={this.handleInputChange}
            isInvalid={!!validationErrors.get('ethnicity')}
          >
            <option value={Ethnicity.NONE}>Select Ethnicity</option>
            {
              Client.ETHNICITIES.map((ethnicity: Ethnicity, index: number) => {
                return (
                    <option key={index} value={ethnicity} >
                      {ethnicity}
                    </option>
                  );
                })
            }
          </Form.Control>
        </Col>  
      </Form.Group>
    );
  }

  elevation(client: Client, validationErrors: any, col1: number, col2: number) {
    let elevationString = '';
    if (client.elevation != null) {
      elevationString = client.elevation.toString();
    }

    const panelTooltip = (
      <Tooltip 
        id="modal-tooltip"
      >Click this icon to learn more about how elevation is used and how to look it up.
      </Tooltip>
    );

    return (
      <Form.Group as={Row} controlId="elevationGroup">
        <Form.Label column sm={col1}>
          Elevation&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon className="external-link" icon="external-link-alt"  onClick={this.openElevationInfoWindow} />
          </OverlayTrigger>
        </Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
                name="elevation"
                maxLength={5}
                onChange={this.handleInputChange}
                placeholder="e.g. 5212"
                type="text"
                value={elevationString}
                isInvalid={!!validationErrors.get('elevation')}
            />
            <InputGroup.Append>
              <InputGroup.Text>feet</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }

  cigaretteSmoker(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="cigaretteSmokerGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Cigarette Smoker?</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="cigaretteSmoker1"
            name="cigaretteSmoker" 
            value="false"
            checked={client.cigaretteSmoker != null && !client.cigaretteSmoker}  
            onChange={this.handleInputChange}
            label="No" 
            type="radio"
            isInvalid={!!validationErrors.get('cigaretteSmoker')}
          />
          <Form.Check
            inline
            id="cigaretteSmoker2"
            name="cigaretteSmoker"
            value="true"
            checked={client.cigaretteSmoker}   
            onChange={this.handleInputChange}
            label="Yes" 
            type="radio"
            isInvalid={!!validationErrors.get('cigaretteSmoker')}
          /> 
        </Col>
      </Form.Group>
    );
  }

  cigarettesSmoked(client: Client, validationErrors: any, col1: number, col2: number) {
    console.log(client);
    if (client.cigaretteSmoker == null || !client.cigaretteSmoker) {
      return null;
    }

    let cigarettesSmokedString = '';
    if (client.cigarettesSmoked != null) {
      cigarettesSmokedString = client.cigarettesSmoked.toString();
    }

    return (
      <Form.Group as={Row} controlId="cigarettesSmoked">
        <Form.Label column sm={col1}>Cigarettes Smoked</Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
                name="cigarettesSmoked"
                size="sm"
                maxLength={2}
                onChange={this.handleInputChange}
                placeholder="e.g. 20"
                type="text"
                value={cigarettesSmokedString}
                isInvalid={!!validationErrors.get('cigarettesSmoked')}
            />
            <InputGroup.Append>
              <InputGroup.Text>per day</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }

  alcoholDrinker(client: Client, validationErrors: any, col1: number, col2: number) {
    return (
      <Form.Group as={Row} controlId="alcoholDrinkerGroup">
        <Form.Label column sm={col1} className="labsmarts-form-check-label">Alcohol Drinker?</Form.Label>
        <Col sm={col2}>
          <Form.Check
            className="form-check-inline-first"
            inline
            id="alcoholDrinker1"
            name="alcoholDrinker" 
            value="false"
            checked={client.alcoholDrinker != null && !client.alcoholDrinker}  
            onChange={this.handleInputChange}
            label="No" 
            type="radio"
            isInvalid={!!validationErrors.get('alcoholDrinker')}
          />
          <Form.Check
            inline
            id="alcoholDrinker2"
            name="alcoholDrinker"
            value="true"
            checked={client.alcoholDrinker}   
            onChange={this.handleInputChange}
            label="Yes" 
            type="radio"
            isInvalid={!!validationErrors.get('alcoholDrinker')}
          /> 
        </Col>
      </Form.Group>
    );
  }

  standardDrinks(client: Client, validationErrors: any, col1: number, col2: number) {
    console.log(client);
    if (client.alcoholDrinker == null || !client.alcoholDrinker) {
      return null;
    }

    let standardDrinksString = '';
    if (client.standardDrinks != null) {
      standardDrinksString = client.standardDrinks.toString();
    }

    const panelTooltip = (
      <Tooltip 
        id="modal-tooltip"
      >Click this icon to learn what defines a Standard Drink.
      </Tooltip>
    );

    return (
      <Form.Group as={Row} controlId="standardDrinks">
        <Form.Label column sm={col1}>
          Standard Drinks&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon className="external-link" icon="external-link-alt"  onClick={this.openStandardDrinkInfoWindow} />
          </OverlayTrigger>
        </Form.Label>
        <Col sm={col2}>
          <InputGroup>
            <Form.Control
                name="standardDrinks"
                size="sm"
                maxLength={2}
                onChange={this.handleInputChange}
                placeholder="e.g. 4"
                type="text"
                value={standardDrinksString}
                isInvalid={!!validationErrors.get('standardDrinks')}
            />
            <InputGroup.Append>
              <InputGroup.Text>per week</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Col>
      </Form.Group>
    );
  }

  toggleAddClient() {
    console.log('toggleAdd called....');
    const { clientEntryStore } = this.props;
    clientEntryStore.setExpandAddClient(!this.state.expandAddClient);
  }

  render() {
    const { panelTitle, validationErrors, expandAddClient } = this.state;
    const { client, currentUser } = this.props;
    const col1 = 2, col2 = 3;
    const expandAddClientIconName = expandAddClient ? 'chevron-up' : 'chevron-down';

    console.log(currentUser);
    return (
      <div>
        {this.renderAlerts()}
        <Container fluid>
          <Card>
            <Card.Header onClick={() => this.toggleAddClient()}>
            <FontAwesomeIcon icon={expandAddClientIconName} />&nbsp;&nbsp;{panelTitle}
            </Card.Header>
            { expandAddClient ?
            <>
            <Card.Body>
              {this.renderErrors()}
              <br/>
              <Form noValidate>
                <Form.Group as={Row} column="true" controlId="firstNameGroup">
                  <Form.Label column sm={col1}>First Name</Form.Label>
                  <Col sm={col2}>
                    <Form.Control
                        name="firstName"
                        size="sm"
                        maxLength={50}
                        onChange={this.handleInputChange}
                        placeholder="e.g. MaryAnn"
                        type="text"
                        value={client.firstName}
                        isInvalid={!!validationErrors.get('firstName')}
                    />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} column="true" controlId="lastNameGroup">
                  <Form.Label column sm={col1}>Last Name</Form.Label>
                  <Col sm={col2}>
                    <Form.Control
                        name="lastName"
                        size="sm"
                        maxLength={50}
                        onChange={this.handleInputChange}
                        placeholder="e.g. Marks"
                        type="text"
                        value={client.lastName}
                        isInvalid={!!validationErrors.get('lastName')}
                    />
                  </Col>
                </Form.Group>
                {this.gender(client, validationErrors, col1, col2)}
                {this.menopause(client, validationErrors, col1, 4)}
                {this.trimester(client, validationErrors, col1, col2)}
                {this.dateOfBirth(client, validationErrors, col1, col2)}
                {this.ethnicity(client, validationErrors, col1, col2)}
                {this.elevation(client, validationErrors, col1, col2)}
                {this.cigaretteSmoker(client, validationErrors, col1, col2)}
                {this.cigarettesSmoked(client, validationErrors, col1, col2)}
                {this.alcoholDrinker(client, validationErrors, col1, col2)}
                {this.standardDrinks(client, validationErrors, col1, col2)}
                <Form.Group as={Row}>
                  <Col sm={{ span: 10, offset: 2 }}>
                    <Button id="submitButton" type="submit" onClick={this.saveOrUpdate}>Save</Button>
                    &nbsp;&nbsp;&nbsp;
                    <Button id="clearButton" type="button" disabled={!client.isNew()}
                      variant="outline-primary" onClick={() => this.clearForm(true)}>Clear</Button>
                  </Col>
                </Form.Group>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="important-note">Note: Changes made to an existing client here only apply to all future test results added for that client.</div>
            </Card.Footer>
            </>
            : null 
          }
          </Card>
        </Container>
      </div>
    );
  }
}
